/**
 * Telling the user what happened.
 *
 * Nothing in this product confirmed a write. Creating an area, a house, a
 * family, a person, a donation or a staff member closed the dialog and said
 * nothing, and a failure that was not a field error said nothing either --
 * with the network down, a submission sat on a disabled button forever and
 * then quietly succeeded minutes later on reconnect. A user could not tell a
 * save from a loss.
 *
 * `sonner` was already a dependency and was never wired up. It is wired here
 * once, at the query client, rather than at fifty call sites: every mutation
 * reports through the same path, so nothing can be added later that forgets.
 */
import { toast } from "sonner";

import i18n from "i18next";

import { ApiError } from "@/lib/api/client";

/** A write succeeded. `message` overrides the generic confirmation. */
export function notifySaved(message?: string): void {
  toast.success(message ?? i18n.t("state.saved"));
}

/**
 * The device is offline, so this write is waiting for a connection.
 *
 * Not an error: TanStack holds the mutation and sends it on reconnect, which
 * is the right thing on mobile data. It just has to be visible.
 */
export function notifyQueued(): void {
  toast.info(i18n.t("state.offlineQueued"), { duration: 6000 });
}

/**
 * A write failed for a reason the form itself will not show.
 *
 * Field errors are rendered against their fields and a `FormError` banner
 * carries a business-rule refusal, so those are left alone -- a toast on top
 * of an inline message is noise. What reaches here is everything with nowhere
 * else to appear: a dropped connection, a 500, a permission error on a screen
 * with no error slot.
 */
export function notifyFailed(error: unknown): void {
  if (error instanceof ApiError && error.fieldErrors.length > 0) return;
  const code = error instanceof ApiError ? error.code : "NETWORK";
  toast.error(i18n.t([`errors.${code}`, "errors.UNKNOWN_ERROR"]));
}
